import React, { useEffect, useState } from 'react';
import { Modal, Descriptions, Tag, Button, Spin, Space, notification, Divider, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';

interface Props {
  open: boolean;
  campaignId: string | null;
  onClose: () => void;
  onApprove: (id: string) => Promise<void>;
  onReject: (id: string) => Promise<void>;
  getByID: (id: string) => Promise<any>;
  canApprove: boolean;
  canReject: boolean;
}

const CampaignViewModal: React.FC<Props> = ({
  open, campaignId, onClose, onApprove, onReject, getByID, canApprove, canReject,
}) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);

  // Fetch fileName from fileId using the files API
  const fetchFileName = async (fileId: number) => {
    try {
      const token = localStorage.getItem('loginToken');
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/file/get-uploaded-files/0/10000`,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      if (!res.ok) return;
      const list = await res.json();
      const files = Array.isArray(list) ? list : list?.content || [];
      const found = files.find((f: any) => f.fileId === fileId);
      setFileName(found?.fileName || null);
    } catch {
      setFileName(null);
    }
  };

  useEffect(() => {
    if (!open || !campaignId) return;
    setFileName(null);
    const fetch = async () => {
      setLoading(true);
      try {
        const res = await getByID(campaignId);
        setData(res);
        // Fetch filename if fileId exists
        if (res?.fileId) {
          fetchFileName(res.fileId);
        }
      } catch {
        notification.error({ message: 'Failed to load campaign details' });
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [open, campaignId]);

  const handleApprove = async () => {
    if (!campaignId) return;
    setActionLoading(true);
    await onApprove(campaignId);
    setActionLoading(false);
    onClose();
  };

  const handleReject = async () => {
    if (!campaignId) return;
    setActionLoading(true);
    await onReject(campaignId);
    setActionLoading(false);
    onClose();
  };

  const statusColor: Record<string, string> = {
    APPROVED: 'green', PENDING: 'orange', REJECTED: 'red',
    COMPLETED: 'blue', EXECUTED: 'purple', CANCELLED: 'default',
  };

  const isPending = data?.campaignStatus === 'PENDING';

  // Campaign Winners Table Columns
  const winnerColumns: ColumnsType<any> = [
    {
      title: 'Position',
      dataIndex: 'winningPosition',
      key: 'winningPosition',
      width: 80,
      render: (pos: number) => <Tag color="blue">#{pos}</Tag>,
    },
    {
      title: 'Prize Type',
      dataIndex: 'prizeType',
      key: 'prizeType',
      width: 100,
    },
    {
      title: 'Prize Details',
      key: 'prizeDetails',
      render: (record: any) => (
        record.prizeType === 'Prize' 
          ? `OMR ${record.prizeAmount || 0}` 
          : record.prizeGift || '-'
      ),
    },
    {
      title: 'Date Range',
      key: 'dateRange',
      render: (record: any) => (
        `${record.winningStartRange || '-'} to ${record.winningEndRange || '-'}`
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 120,
      render: (record: any) => (
        <Space direction="vertical" size={0}>
          <Tag color={record.isAnnounced ? 'green' : 'default'} style={{ fontSize: 11 }}>
            {record.isAnnounced ? 'Announced' : 'Not Announced'}
          </Tag>
          <Tag color={record.isExecuted ? 'purple' : 'default'} style={{ fontSize: 11 }}>
            {record.isExecuted ? 'Executed' : 'Not Executed'}
          </Tag>
        </Space>
      ),
    },
  ];

  // Campaign Filters Table Columns
  const filterColumns: ColumnsType<any> = [
    {
      title: 'Filter Field ID',
      dataIndex: 'filterFieldId',
      key: 'filterFieldId',
      width: 120,
    },
    {
      title: 'Filter Field Name',
      dataIndex: 'filterFieldName',
      key: 'filterFieldName',
      width: 150,
      render: (v: string) => v || '-',
    },
    {
      title: 'Operation',
      dataIndex: 'filterOperation',
      key: 'filterOperation',
      width: 100,
      render: (op: string) => <Tag>{op}</Tag>,
    },
    {
      title: 'Value',
      dataIndex: 'filterValue',
      key: 'filterValue',
    },
  ];

  return (
    <Modal
      title="Campaign Details"
      open={open}
      onCancel={onClose}
      width={900}
      footer={
        isPending ? (
          <Space>
            {canReject && (
              <Button danger loading={actionLoading} onClick={handleReject}>
                Reject
              </Button>
            )}
            {canApprove && (
              <Button type="primary" loading={actionLoading} onClick={handleApprove}>
                Approve
              </Button>
            )}
            <Button onClick={onClose}>Close</Button>
          </Space>
        ) : (
          <Button onClick={onClose}>Close</Button>
        )
      }
    >
      <Spin spinning={loading}>
        {data && (
          <>
            {/* Basic Campaign Information */}
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="Campaign ID">
                {data.campaignId}
              </Descriptions.Item>
              <Descriptions.Item label="Campaign Name">
                {data.campaignName}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={statusColor[data.campaignStatus] || 'default'}>
                  {data.campaignStatus}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Approved">
                <Tag color={data.isApproved ? 'green' : 'red'}>
                  {data.isApproved ? 'Yes' : 'No'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Start Date">
                {data.startDate}
              </Descriptions.Item>
              <Descriptions.Item label="Start Time">
                {data.startTime}
              </Descriptions.Item>
              <Descriptions.Item label="End Date">
                {data.endDate}
              </Descriptions.Item>
              <Descriptions.Item label="End Time">
                {data.endTime}
              </Descriptions.Item>
              <Descriptions.Item label="Winner Frequency" span={2}>
                {data.winnerFrequency}
              </Descriptions.Item>
              <Descriptions.Item label="No of Winners">
                {data.noOfWinners}
              </Descriptions.Item>
              <Descriptions.Item label="Total Winner Slots">
                {data.campaignWinners?.length ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Winner Selection">
                {data.winnerAnnouncementSelection || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="File ID">
                {data.fileId || '-'}
              </Descriptions.Item>
                <Descriptions.Item label="File Name">
                  {fileName}
              </Descriptions.Item>
              <Descriptions.Item label="Institution ID">
                {data.institutionId || '-'}
              </Descriptions.Item>
              {data.isMockCampaign && (
                <Descriptions.Item label="Mock Campaign">
                  <Tag color="orange">Yes</Tag>
                </Descriptions.Item>
              )}
              {data.approvedAt && (
                <Descriptions.Item label="Approved At" span={2}>
                  {new Date(data.approvedAt).toLocaleString()}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Description" span={2}>
                {data.description || '-'}
              </Descriptions.Item>
            </Descriptions>

            {/* Exclusion Settings */}
            <Divider orientation="left" style={{ fontSize: 14, fontWeight: 600 }}>
              Exclusion Settings
            </Divider>
            <Descriptions bordered column={3} size="small">
              <Descriptions.Item label="Blacklisted Excluded">
                <Tag color={data.blackListedCustomerExcluded ? 'green' : 'red'}>
                  {data.blackListedCustomerExcluded ? 'Yes' : 'No'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Winner Branch Excluded">
                <Tag color={data.isWinnerBranchExcluded ? 'green' : 'red'}>
                  {data.isWinnerBranchExcluded ? 'Yes' : 'No'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Winning Customer Excluded">
                <Tag color={data.winningCustomerExcluded ? 'green' : 'red'}>
                  {data.winningCustomerExcluded ? 'Yes' : 'No'}
                </Tag>
              </Descriptions.Item>
            </Descriptions>

            {/* Campaign Filters */}
            {data.campaignFilters && data.campaignFilters.length > 0 && (
              <>
                <Divider orientation="left" style={{ fontSize: 14, fontWeight: 600 }}>
                  Campaign Filters ({data.campaignFilters.length})
                </Divider>
                <Table
                  columns={filterColumns}
                  dataSource={data.campaignFilters}
                  rowKey="campaignFilterId"
                  pagination={false}
                  size="small"
                  bordered
                />
              </>
            )}

            {/* Campaign Winners */}
            {/* {data.campaignWinners && data.campaignWinners.length > 0 && (
              <>
                <Divider orientation="left" style={{ fontSize: 14, fontWeight: 600 }}>
                  Campaign  ({data.campaignWinners.length})
                </Divider>
                <Table
                  columns={winnerColumns}
                  dataSource={data.campaignWinners}
                  rowKey="campaignWinnerId"
                  pagination={false}
                  size="small"
                  bordered
                  expandable={{
                    expandedRowRender: (record) => (
                      <Descriptions size="small" column={2} bordered>
                        <Descriptions.Item label="Winner ID">
                          {record.campaignWinnerId}
                        </Descriptions.Item>
                        {record.announcedAt && (
                          <Descriptions.Item label="Announced At" span={2}>
                            {new Date(record.announcedAt).toLocaleString()} by {record.announcedByName || '-'}
                          </Descriptions.Item>
                        )}
                        {record.executedAt && (
                          <Descriptions.Item label="Executed At" span={2}>
                            {new Date(record.executedAt).toLocaleString()} by {record.executedByName || '-'}
                          </Descriptions.Item>
                        )}
                        {record.transactionIds && (
                          <Descriptions.Item label="Transaction IDs" span={2}>
                            {record.transactionIds}
                          </Descriptions.Item>
                        )}
                      </Descriptions>
                    ),
                    rowExpandable: (record) => true,
                  }}
                />
              </>
            )} */}
          </>
        )}
      </Spin>
    </Modal>
  );
};

export default CampaignViewModal;
